import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div className="border-t border-cord/40 p-3 bg-white shrink-0">
        <div className="flex gap-2 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 px-4 py-3 text-sm',
              'bg-cream border border-cord/60 rounded-full',
              'text-ink placeholder:text-muted/60',
              'focus:outline-none focus:ring-2 focus:ring-maia/25 focus:border-maia',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-all'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !input.trim()}
            className={cn(
              'p-3 bg-maia text-white rounded-full flex-shrink-0',
              'hover:bg-maia-dark active:scale-95',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
              'transition-all duration-200',
              'shadow-md shadow-maia/20'
            )}
            aria-label={t('chatbot.send')}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    )
  }
)
